import React from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import LoginButton from './LoginButton';
import { Box, Typography, Paper, CircularProgress } from '@mui/material';

const ProtectedRoute = ({ children, requiredRole = null, requiredPermission = null }) => {
  const { isAuthenticated, user, hasPermission } = useAuth();
  const { canAccessAdmin, isLoading: permissionsLoading } = usePermissions();

  if (!isAuthenticated) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          flexDirection: 'column',
          gap: 2
        }}
      >
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h5" gutterBottom>
            Authentication Required
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            You need to be logged in to access this page.
          </Typography>
          {/* Come back here after signing in (e.g. the chat a notification opened). */}
          <LoginButton returnTo={`${window.location.pathname}${window.location.search}`} />
        </Paper>
      </Box>
    );
  }

  // Check role-based access if required
  if (requiredRole && user?.role !== requiredRole) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          flexDirection: 'column' 
        }}
      >
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h5" gutterBottom>
            Access Denied
          </Typography>
          <Typography variant="body1">
            You don't have permission to access this page.
          </Typography>
        </Paper>
      </Box>
    );
  }

  // Check permission-based access if required
  if (requiredPermission) {
    const hasJwtPermission = hasPermission(requiredPermission);
    // For admin:tenant, also check backend permissions (supports roles like Spares Coordinator
    // whose admin access is determined by the backend, not JWT claims)
    const hasBackendPermission = requiredPermission === 'admin:tenant' && canAccessAdmin();

    if (!hasJwtPermission && !hasBackendPermission) {
      // Still loading backend permissions — show loading indicator
      if (requiredPermission === 'admin:tenant' && permissionsLoading) {
        return (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
            <CircularProgress />
          </Box>
        );
      }
      return (
        <Box 
          sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '100vh',
            flexDirection: 'column' 
          }}
        >
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h5" gutterBottom>
              Access Denied
            </Typography>
            <Typography variant="body1">
              You don't have the required permission to access this page.
            </Typography>
          </Paper>
        </Box>
      );
    }
  }

  return children;
};

export default ProtectedRoute;
